/**
 * VariableInputsSection Component
 *
 * Accordion section for defining parameters from V2 funnel variables.
 * Part of the "DEFINE → USE" pattern: defines parameters that can be used in destinations.
 *
 * V2 funnels store user inputs in FunnelVariable objects keyed by variableId,
 *
 * @module webplacements/components/editors/sources
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import TuneIcon from '@mui/icons-material/Tune';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { ParameterDefinition } from '@modules/webplacements/types/webplacements.types';
import type { FunnelVariable } from '@segmently/schema-models/v2';

export interface VariableInputsSectionProps {
  /** Current variable input parameters (filtered from all parameters) */
  parameters: ParameterDefinition[];
  /** Called when parameters change */
  onChange: (params: ParameterDefinition[]) => void;
  /** Available variables from V2 funnel (FlowDocument.variables) */
  availableVariables?: FunnelVariable[];
  /** Whether the editor is disabled */
  disabled?: boolean;
}

interface NewParamState {
  /** Selected variable ID */
  variableId: string;
  /** Internal key for {{key}} placeholder */
  key: string;
}

const initialNewParam: NewParamState = {
  variableId: '',
  key: '',
};

/** Map VariableType to ParameterDefinition dataType */
function mapVariableDataType(varType: string): 'string' | 'number' | 'boolean' {
  switch (varType) {
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    default:
      return 'string';
  }
}

/**
 * VariableInputsSection Component
 *
 * Provides a table-based interface for defining V2 variable input parameters.
 * Uses FunnelVariable[] loaded from FlowDocument.variables.
 */
export const VariableInputsSection: React.FC<VariableInputsSectionProps> = ({
  parameters,
  onChange,
  availableVariables = [],
  disabled = false,
}) => {
  const theme = useTheme();
  const [newParam, setNewParam] = useState<NewParamState>(initialNewParam);
  const [isAdding, setIsAdding] = useState(false);

  /**
   * Build dropdown options from available variables
   * Excludes variables that are already added as parameters
   */
  const variableOptions = useMemo(() => {
    const usedVariableIds = new Set(parameters.map(p => p.sourceDetails.variableId));
    return availableVariables.filter(v => !usedVariableIds.has(v.id));
  }, [availableVariables, parameters]);

  const handleAddClick = useCallback(() => {
    setIsAdding(true);
    setNewParam(initialNewParam);
  }, []);

  const handleCancelAdd = useCallback(() => {
    setIsAdding(false);
    setNewParam(initialNewParam);
  }, []);

  /**
   * Handle variable selection - auto-suggest key from variable name
   */
  const handleVariableChange = useCallback((variableId: string) => {
    const variable = availableVariables.find(v => v.id === variableId);
    if (!variable) return;

    // Auto-suggest key from variable name (sanitized)
    const suggestedKey = variable.name.toLowerCase().replace(/[^a-z0-9_]/g, '_');

    setNewParam(prev => ({
      ...prev,
      variableId,
      key: prev.key || suggestedKey,
    }));
  }, [availableVariables]);

  /**
   * Save new parameter
   */
  const handleSaveNew = useCallback(() => {
    if (!newParam.variableId || !newParam.key) return;

    // Check for duplicate keys
    if (parameters.some(p => p.key === newParam.key)) {
      return;
    }

    const variable = availableVariables.find(v => v.id === newParam.variableId);
    if (!variable) return;

    const newDefinition: ParameterDefinition = {
      key: newParam.key,
      source: 'variable',
      sourceDetails: {
        variableId: variable.id,
        variableType: variable.type,
      },
      label: variable.name,
      dataType: mapVariableDataType(variable.type),
    };

    onChange([...parameters, newDefinition]);
    setIsAdding(false);
    setNewParam(initialNewParam);
  }, [newParam, parameters, onChange, availableVariables]);

  const handleDelete = useCallback(
    (key: string) => {
      onChange(parameters.filter(p => p.key !== key));
    },
    [parameters, onChange]
  );

  /**
   * Get display label for a saved parameter
   */
  const getParamDisplayLabel = useCallback((param: ParameterDefinition): string => {
    const variable = availableVariables.find(v => v.id === param.sourceDetails.variableId);
    return variable?.name || param.label || param.sourceDetails.variableId || 'Unknown';
  }, [availableVariables]);

  /**
   * Get variable type display string
   */
  const getVariableTypeLabel = useCallback((param: ParameterDefinition): string => {
    return param.sourceDetails.variableType || 'string';
  }, []);

  return (
    <Box>
      {/* Header with Add button on the right */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <TuneIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
        <Typography variant="body2" color="text.secondary">
          Capture user inputs from V2 funnel variables
        </Typography>
        <Tooltip title="V2 variables are typed data slots (string, number, enum, etc.) that collect user input during the funnel flow. Select which variables to pass to Stripe metadata, redirect URL, or webhooks.">
          <InfoOutlinedIcon fontSize="small" color="action" />
        </Tooltip>
        {/* Spacer to push button to the right */}
        <Box sx={{ flex: 1 }} />
        {!isAdding && (
          <Button
            data-testid="wp-variable-add-btn"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            disabled={disabled || variableOptions.length === 0}
          >
            Add Variable
          </Button>
        )}
      </Box>

      {/* Parameters Table */}
      {parameters.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '35%' }}>Variable</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '15%' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '35%' }}>Internal Key</TableCell>
                <TableCell align="right" sx={{ width: '15%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parameters.map((param, index) => (
                <TableRow key={param.key} hover data-testid={`wp-variable-row-${index}`}>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {getParamDisplayLabel(param)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getVariableTypeLabel(param)}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`{{${param.key}}}`}
                      size="small"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        bgcolor: theme.palette.action.selected,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      data-testid={`wp-variable-delete-${index}`}
                      size="small"
                      onClick={() => handleDelete(param.key)}
                      disabled={disabled}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add New Row */}
      {isAdding ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 2,
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Variable selection dropdown */}
          <FormControl size="small" fullWidth>
            <InputLabel>Variable</InputLabel>
            <Select
              data-testid="wp-variable-select"
              value={newParam.variableId}
              onChange={(e) => handleVariableChange(e.target.value)}
              label="Variable"
            >
              {variableOptions.length > 0 ? (
                variableOptions.map((variable) => (
                  <MenuItem key={variable.id} value={variable.id}>
                    <Box>
                      <Typography variant="body2">{variable.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {variable.type}{variable.description ? ` — ${variable.description}` : ''}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled value="">
                  No variables available
                </MenuItem>
              )}
            </Select>
            <FormHelperText>Select a funnel variable to capture</FormHelperText>
          </FormControl>

          <TextField
            data-testid="wp-variable-key-input"
            label="Internal Key"
            value={newParam.key}
            onChange={(e) =>
              setNewParam((prev) => ({
                ...prev,
                key: e.target.value.replace(/[^a-zA-Z0-9_]/g, ''),
              }))
            }
            size="small"
            fullWidth
            required
            error={!newParam.key && !!newParam.variableId}
            placeholder="user_email"
            helperText="Used as {{key}} in destinations"
            InputProps={{
              sx: { fontFamily: 'monospace' },
            }}
          />

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button data-testid="wp-variable-cancel-btn" size="small" onClick={handleCancelAdd}>
              Cancel
            </Button>
            <Button
              data-testid="wp-variable-save-btn"
              size="small"
              variant="contained"
              onClick={handleSaveNew}
              disabled={!newParam.variableId || !newParam.key}
            >
              Add
            </Button>
          </Box>
        </Box>
      ) : null}

      {/* Empty State */}
      {parameters.length === 0 && !isAdding && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            textAlign: 'center',
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No variable inputs defined yet.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {availableVariables.length === 0
              ? 'Select a V2 onboarding first to add variable inputs.'
              : 'Add funnel variables to pass to Stripe, redirect URL, or webhooks.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default VariableInputsSection;
